/**
 * Iter123k241 · Compressione client-side delle immagini prima dell'upload.
 *
 * Perché: evita invii fallimentari per allegati troppo pesanti, riduce il
 * traffico, previene OOM lato pod produzione (foto > 10MB in RAM come base64).
 *
 * Come: usa `<canvas>` nativo del browser per ridimensionare l'immagine al
 * lato lungo massimo configurato e riserializza in JPEG con qualità impostabile.
 * Mantiene il nome del file ma cambia estensione in `.jpg` quando la ricodifica
 * sostituisce PNG/WEBP/HEIC. I file non-immagine (PDF, DOCX, ZIP, ...) sono
 * ritornati così come sono.
 *
 * Uso tipico:
 *   import { compressImage } from "../lib/compressImage";
 *   const prepared = await compressImage(file);
 *   fd.append("file", prepared);
 */

const DEFAULT_MAX_SIDE = 1920;       // lato lungo in px → 2K visualmente nitido
const DEFAULT_QUALITY = 0.82;         // JPEG quality
const DEFAULT_MAX_BYTES = 1_500_000;  // 1.5 MB · soglia sotto cui NON ricomprimiamo
const COMPRESSIBLE_MIME = /^image\/(jpeg|jpg|png|webp)$/i;

/**
 * Ritorna un File (immagine jpeg) ridimensionato/ricompresso, oppure il File
 * originale se non è un'immagine supportata o se è già sotto soglia.
 *
 * @param {File} file  file da upload
 * @param {object} opts { maxSide, quality, maxBytes }
 * @returns {Promise<File>}
 */
export async function compressImage(file, opts = {}) {
  if (!file || typeof file !== "object" || !file.type) return file;
  const maxSide = opts.maxSide || DEFAULT_MAX_SIDE;
  const quality = opts.quality || DEFAULT_QUALITY;
  const maxBytes = opts.maxBytes || DEFAULT_MAX_BYTES;

  // Non è un'immagine supportata → ritorno subito l'originale
  if (!COMPRESSIBLE_MIME.test(file.type)) return file;

  // Se è già sotto soglia non vale la pena ricomprimere
  if (file.size && file.size <= maxBytes) return file;

  try {
    const dataUrl = await _readAsDataURL(file);
    const img = await _loadImage(dataUrl);

    let { width, height } = img;
    if (width <= maxSide && height <= maxSide) {
      // Nessun bisogno di ridimensionare, prova solo la ricodifica JPEG se diversa
      if (/^image\/jpeg$/i.test(file.type)) return file;
    } else {
      // Scala mantenendo le proporzioni sul lato lungo
      if (width >= height) {
        height = Math.round((height * maxSide) / width);
        width = maxSide;
      } else {
        width = Math.round((width * maxSide) / height);
        height = maxSide;
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    // Riempimento bianco per PNG trasparenti → JPEG non supporta alpha
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality),
    );
    if (!blob) return file;
    // Se la compressione non ha portato guadagni, ritorna l'originale
    if (blob.size >= file.size) return file;

    const newName = _renameToJpg(file.name);
    return new File([blob], newName, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (_e) {
    // In qualsiasi errore (CORS, decode, memoria) ritorno l'originale
    return file;
  }
}

/**
 * Comprime un array di file (chiamando compressImage su ciascuno).
 * Mantiene l'ordine; i non-immagine passano invariati.
 */
export async function compressImageList(files, opts = {}) {
  if (!files || !files.length) return files;
  const arr = Array.from(files);
  return Promise.all(arr.map((f) => compressImage(f, opts)));
}

function _readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(file);
  });
}

function _loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image_decode_failed"));
    img.src = src;
  });
}

function _renameToJpg(name) {
  if (!name) return "compressed.jpg";
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  return `${base}.jpg`;
}
